import { useState, useRef, useEffect } from "react";
import { Calendar, Check, ChevronDown } from "lucide-react";
import type { Period } from "./types";
import { buildFinancePeriods, createCustomPeriod, formatDateForInput } from "./periods";

export function PeriodSelector({ value, onChange }: { value: Period; onChange: (v: Period) => void }) {
  const [open, setOpen] = useState(false);
  const [customFrom, setCustomFrom] = useState(() => formatDateForInput(value.from));
  const [customTo, setCustomTo] = useState(() => formatDateForInput(value.to));
  const ref = useRef<HTMLDivElement>(null);
  const periods = buildFinancePeriods();
  const current = value;

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  useEffect(() => {
    if (!open) return;
    setCustomFrom(formatDateForInput(value.from));
    setCustomTo(formatDateForInput(value.to));
  }, [open, value.from, value.to]);

  const applyCustomPeriod = () => {
    if (!customFrom || !customTo) return;
    onChange(createCustomPeriod(customFrom, customTo));
    setOpen(false);
  };

  return (
    <div ref={ref} className="relative">
      <button
        onClick={() => setOpen(!open)}
        className="flex h-10 items-center gap-2 whitespace-nowrap rounded-full border border-gray-200 bg-white px-3 text-sm font-semibold text-gray-800 shadow-sm transition hover:bg-gray-50"
        type="button"
      >
        <Calendar className="w-4 h-4 text-[#2b5ce6]" />
        <span className="text-gray-900">{current.label}</span>
        <ChevronDown className="w-4 h-4 text-gray-400" />
      </button>

      {open && (
        <div className="absolute right-0 top-full z-20 mt-2 w-[320px] rounded-lg border border-gray-200 bg-white p-2 shadow-xl">
          {periods.map(p => (
            <button
              key={p.id}
              onClick={() => { onChange(p); setOpen(false); }}
              className={`flex w-full items-center justify-between rounded-md px-3 py-2 text-left text-sm transition hover:bg-gray-50 ${p.id === value.id ? "text-gray-900" : "text-gray-600"}`}
              type="button"
            >
              <span>{p.label}</span>
              {p.id === value.id ? <Check className="h-4 w-4 text-[#2b5ce6]" /> : null}
            </button>
          ))}

          <div className="mt-2 border-t border-gray-100 pt-3">
            <p className="px-2 text-[11px] font-semibold uppercase tracking-wide text-gray-400">
              Rango personalizado
            </p>
            <div className="mt-2 grid grid-cols-2 gap-2">
              <label className="text-xs font-medium text-gray-500">
                Desde
                <input
                  type="date"
                  value={customFrom}
                  onChange={(event) => setCustomFrom(event.target.value)}
                  className="mt-1 h-10 w-full rounded-lg border border-gray-200 px-2 text-sm text-gray-800 outline-none focus:border-[#2b5ce6]"
                />
              </label>
              <label className="text-xs font-medium text-gray-500">
                Hasta
                <input
                  type="date"
                  value={customTo}
                  onChange={(event) => setCustomTo(event.target.value)}
                  className="mt-1 h-10 w-full rounded-lg border border-gray-200 px-2 text-sm text-gray-800 outline-none focus:border-[#2b5ce6]"
                />
              </label>
            </div>
            <button
              onClick={applyCustomPeriod}
              disabled={!customFrom || !customTo}
              className="mt-3 inline-flex h-10 w-full items-center justify-center rounded-lg bg-[#2b5ce6] text-sm font-semibold text-white transition hover:bg-[#214ec7] disabled:cursor-not-allowed disabled:opacity-50"
              type="button"
            >
              Aplicar fechas
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
